/**
 * SwitcherPill — shared dropdown-switcher trigger + popover chrome.
 *
 *   ┌──────────────────┐
 *   │ <label> ▾        │   ← hyperlink-styled trigger (transparent
 *   └──────────────────┘     bg, underlined on hover)
 *           │
 *           ▼
 *   ┌──────────────────┐
 *   │ … consumer …    │   ← popover panel; consumer renders the
 *   │   menu items     │     contents (items / search / footer);
 *   └──────────────────┘     primitive owns the bg / border / shadow.
 *
 * Used by:
 *   - features/projects/components/ProjectHeader (project switcher)
 *   - features/vulnerability_management/Report (per-scan version switcher)
 *
 * The primitive owns the trigger's hyperlink pill, the chevron icon,
 * the popover positioning + visual chrome, the outside-click +
 * Escape close handlers. The consumer owns: open state (controlled
 * via `isOpen` / `onToggle` / `onClose`), the trigger label text,
 * and the dropdown body (rendered as `children`).
 *
 * Lifted from the inline implementation at ProjectHeader.jsx + the
 * smaller version-dropdown in Report.jsx; both consumers had
 * duplicated the same outside-click / Escape / popover-position
 * boilerplate.
 */
import React, { useEffect, useRef } from 'react';
import { Icon } from '../../ui/Icon';
import styles from './SwitcherPill.module.css';

const cn = (...names) => names.filter(Boolean).join(' ');

/**
 * @param {object} props
 * @param {React.ReactNode}  props.label                 Trigger label.
 * @param {boolean}          props.isOpen                Controlled open state.
 * @param {() => void}       props.onToggle              Fired on trigger click.
 * @param {() => void}       props.onClose               Fired on outside-click / Escape.
 * @param {string}           [props.triggerTitle]        `title` attr on the trigger button.
 * @param {string}           [props.triggerAriaLabel]    `aria-label` on the trigger button.
 * @param {React.ReactNode}  props.children              Dropdown body (consumer-rendered).
 * @param {string}           [props.className]           Outer wrapper class.
 * @param {string}           [props.triggerClassName]    Trigger button class override.
 * @param {string}           [props.labelClassName]      Label span class override.
 * @param {string}           [props.dropdownClassName]   Dropdown popover class override
 *                                                       (e.g., to set a fixed width).
 */
const SwitcherPill = ({
  label,
  isOpen,
  onToggle,
  onClose,
  triggerTitle,
  triggerAriaLabel,
  children,
  className,
  triggerClassName,
  labelClassName,
  dropdownClassName,
}) => {
  const wrapperRef = useRef(null);

  // Outside-click + Escape close. Listeners only attached while
  // open so an idle switcher costs nothing.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleMouseDown = (e) => {
      if (!wrapperRef.current?.contains(e.target)) onClose?.();
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <div ref={wrapperRef} className={cn(styles.wrapper, className)}>
      <button
        type="button"
        className={cn(styles.trigger, triggerClassName)}
        onClick={onToggle}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={triggerTitle}
        aria-label={triggerAriaLabel}
      >
        <span className={cn(styles.label, labelClassName)}>{label}</span>
        <Icon name="chevron-down" size={14} />
      </button>
      {isOpen && (
        <div className={cn(styles.dropdown, dropdownClassName)} role="menu">
          {children}
        </div>
      )}
    </div>
  );
};

export default SwitcherPill;
